import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';

function BuildingPersonalWebsite() {
    const note = {
        title: 'Building a Personal Website from Scratch',
        date: '2024-03-28',
        description: 'Experience sharing on building a modern personal website using React, Tailwind CSS, and Next.js.',
        tags: ['React', 'Web Development', 'Tutorial'],
        content: `
            Building a personal website is an exciting journey that allows you to showcase your work and share your thoughts with the world. In this post, I'll share my experience of building this website from scratch using modern web technologies.

            ## Technologies Used

            - React for the frontend framework
            - Tailwind CSS for styling
            - Next.js for server-side rendering
            - MDX for writing content

            ## The Process

            I started by setting up a basic React project using Create React App. Then, I added Tailwind CSS for styling, which has been a game-changer in terms of development speed and consistency.

            ## Key Features

            - Dark mode support
            - Responsive design
            - Fast page loads
            - SEO optimization

            ## Lessons Learned

            Building this website taught me a lot about modern web development practices and the importance of user experience.
        `
    };

    return (
        <Layout maxWidth="max-w-3xl" bg="bg-primary-light dark:bg-primary-dark">
            <article className="prose dark:prose-invert max-w-none">
                {/* Back button */}
                <Link
                    to="/notes"
                    className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-8"
                >
                    ← Back to Notes
                </Link>

                {/* Article header */}
                <header className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        {note.title}
                    </h1>
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <time dateTime={note.date}>
                            {new Date(note.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        </time>
                        <div className="flex gap-2">
                            {note.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </header>

                {/* Article content */}
                <div className="mt-8 space-y-6 text-gray-700 dark:text-gray-300">
                    {note.content.split('\n\n').map((paragraph, index) => (
                        <p key={index} className="whitespace-pre-wrap">
                            {paragraph.trim()}
                        </p>
                    ))}
                </div>

                {/* Article footer */}
                <footer className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <Link
                            to="/notes"
                            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        >
                            ← Back to Notes
                        </Link>
                        <div className="flex gap-4">
                            <button
                                onClick={() => {/* Add share functionality */ }}
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                                Share
                            </button>
                            <button
                                onClick={() => {/* Add discuss functionality */ }}
                                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                                Discuss
                            </button>
                        </div>
                    </div>
                </footer>
            </article>
        </Layout>
    );
}

export default BuildingPersonalWebsite; 